import { useState } from 'react'
import { useParams } from 'react-router'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from 'tailwind-v4/components/button'
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from 'tailwind-v4/components/field'
import { Input } from 'tailwind-v4/components/input'
import { RichTextEditor } from 'tailwind-v4/components/rich-text-editor'
import { Spinner } from 'tailwind-v4/components/spinner'
import { useIsMobile } from '@/hooks/use-mobile'
import type {
  InboxContextTypes,
  InboxThreadType,
} from '@/state/entities/inbox/inbox.types'
import inboxApi from '@/state/inbox/inbox.slice'
import type { InboxAction } from './modal'
import { EmailContacts } from './email/contacts'

interface PassedProps {
  onComplete: (value: InboxThreadType) => void
  context?: {
    id: string
    type: InboxContextTypes
  }
  createWith: {
    contacts: { email: string; name: string }[]
    serial?: string
    subject: string
    content: string
    actions?: InboxAction[]
  }
}

const validationSchema = z.object({
  subject: z.string().min(1, 'Subject is required'),
  description: z.string().min(1, 'Email content required'),
  contacts: z
    .array(
      z.object({
        name: z.string(),
        email: z
          .string()
          .email('Invalid email format')
          .min(1, 'Email is required'),
      })
    )
    .min(1, 'Add at least one recipient'),
})

type FormValues = z.infer<typeof validationSchema>

function ThreadCreateSendMessageBox({
  createWith,
  onComplete,
  context,
}: PassedProps) {
  const { org_id: orgId = '' } = useParams<{ org_id: string }>()
  const isMobile = useIsMobile()
  const [actionLoadingStates, setActionLoadingStates] = useState<
    Record<number, boolean>
  >({})

  const [createNewThread, { isLoading }] =
    inboxApi.useCreateNewEmailThreadMutation()

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<FormValues>({
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
    defaultValues: {
      description: createWith?.content ?? '',
      subject: createWith?.subject ?? '',
      contacts: createWith?.contacts ?? [],
    },
  })

  const description = watch('description')

  const onSubmit = async (data: FormValues) => {
    const { contacts, subject, description: content } = data

    try {
      const response = await createNewThread({
        orgId,
        data: {
          context_id: context?.id,
          context_type: context?.type,
          serial: createWith.serial,
          contacts,
          subject,
          content,
        },
      }).unwrap()
      if (response.thread) {
        onComplete(response.thread)
      }
      reset()
    } catch (error) {
      console.error('Error creating thread:', error)
    }
  }

  return (
    <form
      className="flex min-h-0 flex-1 flex-col"
      onSubmit={handleSubmit(onSubmit)}
    >
      {/* The scroller carries its own padding + `scroll-py-4` so the outset
          focus rings on the recipients field, subject and editor aren't
          clipped at its box. */}
      <FieldGroup className="min-h-0 flex-1 gap-6 overflow-y-auto px-4 py-4 scroll-py-4">
        <Field>
          <FieldLabel htmlFor="compose-recipients">To</FieldLabel>
          <Controller
            control={control}
            name="contacts"
            render={({ field }) => (
              <EmailContacts
                id="compose-recipients"
                serial={createWith.serial}
                value={field.value}
                onChange={field.onChange}
                invalid={Boolean(errors.contacts)}
              />
            )}
          />
          <FieldError>{errors.contacts?.message}</FieldError>
        </Field>

        <Field>
          <FieldLabel htmlFor="compose-subject">Subject</FieldLabel>
          <Input
            id="compose-subject"
            placeholder="Add a subject"
            aria-invalid={Boolean(errors.subject)}
            {...register('subject')}
          />
          <FieldError>{errors.subject?.message}</FieldError>
        </Field>

        <Field>
          <FieldLabel>Message</FieldLabel>
          <RichTextEditor
            value={description}
            onChange={(html) => {
              setValue('description', html, { shouldValidate: true })
            }}
            placeholder="Write your message"
            // The phone has no room for headings and quotes beside the
            // essentials, so the toolbar reduces rather than wraps.
            toolbar={isMobile ? { headings: false, quote: false } : undefined}
            className="[&_.ProseMirror]:min-h-40"
          />
          <FieldError>{errors.description?.message}</FieldError>
        </Field>

        {createWith.actions?.length ? (
          <div className="flex flex-wrap items-center gap-2">
            {createWith.actions.map((action, index) => {
              const Icon = action.icon
              const actionLoading =
                actionLoadingStates[index] ?? action.isLoading ?? false
              let actionIcon = Icon ? <Icon /> : undefined
              if (actionLoading) {
                actionIcon = <Spinner />
              }
              return (
                <Button
                  key={action.label}
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={actionLoading}
                  leftIcon={actionIcon}
                  onClick={async () => {
                    setActionLoadingStates((prev) => ({
                      ...prev,
                      [index]: true,
                    }))
                    try {
                      await action.onClick((content: string) => {
                        setValue('description', content, {
                          shouldValidate: true,
                        })
                      })
                    } finally {
                      // Drop the key rather than storing `false`, so the
                      // action's own `isLoading` is what's read once our
                      // optimistic flag is spent.
                      setActionLoadingStates((prev) => {
                        const { [index]: _done, ...rest } = prev
                        return rest
                      })
                    }
                  }}
                >
                  {action.label}
                </Button>
              )
            })}
          </div>
        ) : null}
      </FieldGroup>

      <div className="flex flex-shrink-0 justify-end border-t border-border px-4 py-4">
        {/* Auto-width and right-aligned on desktop; the phone gets the
            full-width primary CTA a bottom sheet asks for. */}
        <Button
          type="submit"
          variant="primary"
          size={isMobile ? 'lg' : 'default'}
          className={isMobile ? 'w-full' : undefined}
          disabled={isLoading}
          leftIcon={isLoading ? <Spinner /> : undefined}
        >
          Send
        </Button>
      </div>
    </form>
  )
}

export default ThreadCreateSendMessageBox
